import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import { useTransition } from 'hooks/useTransitions'
import { useSwipeBackForthEffect, useSwipeEffects } from 'hooks/useSwipeEffects'
import { useStopBodyScrollAuto } from 'hooks/useScrollEffects'

import { DropdownProps, groupOptions, Option, TabIndexedOption } from 'components/elements/ArchidektDropdown/types'

import Link from 'components/elements/Link'
import ManaFrontIcon from 'components/misc/customIcons/ManaFont'
import Icon from 'components/elements/Icon'
import CustomIcon from 'components/elements/CustomIcon'
import { SimpleButton } from 'components/formElements/Button'

import { clamp } from 'utils/numbers'

import styles from './drawerMenu.module.scss'

type Props = {
  open: boolean

  onClose: (e?: React.MouseEvent) => void

  maxInitialOpenHeight?: number
  minInitalOpenHeight?: number
} & DropdownProps

const DrawerMenu = (props: Props) => {
  useStopBodyScrollAuto(props.open)

  const [extraOptionId, setExtraOptionId] = useState<string | null>(null)

  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const drawerSwipeRef = useSwipeEffects('vertical', {
    checkCanContinueSwipe: (_e, direction) => {
      if (!open) return false
      if (!direction) return true

      if ((secondaryOptionsRef?.current?.scrollTop || 0) > 0 && direction === 'down') return false

      return (scrollAreaRef?.current?.scrollTop || 0) < 1 && direction === 'down'
    },
    onTouchMove: (desiredOffset: number) => {
      if (!drawerSwipeRef.current) return

      drawerSwipeRef.current.style.transition = 'unset'
      drawerSwipeRef.current.style.transform = `translateY(${desiredOffset < 0 ? -1 * desiredOffset : 0}px)`
    },
    onTouchEnd: (offset, position, speed, direction) => {
      if (!drawerSwipeRef.current) return

      const swipeThresholdMet = offset < -1 * (document.documentElement.clientHeight / 3) || (speed > 0.5 && offset < 0)

      drawerSwipeRef.current.style.transition = ''

      if (!swipeThresholdMet) {
        drawerSwipeRef.current.style.transform = ''

        return
      }

      drawerSwipeRef.current.style.transform = 'translateY(100%)'
      drawerSwipeRef.current.style.animation = 'unset'

      props.onClose()

      setTimeout(() => {
        if (!drawerSwipeRef.current) return

        drawerSwipeRef.current.style.transform = ''
        drawerSwipeRef.current.style.animation = ''
      }, 400)
    },
  })

  const [overlayRef, overlayStatus] = useTransition(props.open, { duration: 200 })
  const [drawerRef, drawerStatus] = useTransition(props.open, {
    enteringClassName: styles.entering,
    exitingClassName: styles.exiting,
  })

  const [primaryOptionsRef, secondaryOptionsRef, primarySecondaryCallbacks] = useSwipeBackForthEffect({
    skipDiplayChangesForSecondary: true,
    checkCanStartSwipe: () => extraOptionId !== null,
    onPrimarySwippedIn: () => setExtraOptionId(null),
  })

  useEffect(() => {
    if (!props.open) setExtraOptionId(null)
  }, [props.open])

  if (!props.open && (overlayStatus === 'hidden' || drawerStatus === 'hidden')) return null

  const extraMenuOption: Option | undefined = props.options?.find(opt => opt.id === extraOptionId)
  const extraOptions = extraMenuOption?.type === 'extras' ? extraMenuOption?.options : []

  const maxOpenHeight = props.maxInitialOpenHeight ? clamp(30, 90, props.maxInitialOpenHeight) : undefined // Only allows max heights between 30dvh and 90dvh
  const minOpenHeight = props.minInitalOpenHeight ? clamp(30, 90, props.minInitalOpenHeight) : undefined // Only allows min heights between 30dvh and 90dvh
  const isScrollingMenu = (scrollAreaRef?.current?.scrollHeight || 0) > (scrollAreaRef?.current?.clientHeight || 0)
  const isSecondaryScrollingMenu =
    (secondaryOptionsRef?.current?.clientHeight || 0) >= (primaryOptionsRef?.current?.clientHeight || 0) &&
    !!extraOptions.length

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (e.target === e.currentTarget) props.onClose()
  }

  const handleOptionClick = (onClick?: () => void, noCloseOnClick?: boolean, closeSecondaryOnClick?: boolean) => {
    if (onClick) onClick()
    if (closeSecondaryOnClick) primarySecondaryCallbacks.handleSwipeInPrimary()
    if (noCloseOnClick) return

    props.onClose()
  }

  const groupedOptions = props.options?.reduce(groupOptions, [] as Option[][])
  const groupedExtras = extraOptions.reduce(groupOptions, [] as Option[][])

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className={styles.overlay}
      onClick={handleClose}
      // Have to style this here for the trantition not to flash on the way in
      style={{ opacity: overlayStatus === 'hidden' ? 0 : undefined }}>
      <div
        ref={ref => {
          drawerRef.current = ref
          drawerSwipeRef.current = ref
        }}
        style={{
          maxHeight: maxOpenHeight ? `${maxOpenHeight}dvh` : '',
          minHeight: minOpenHeight ? `${minOpenHeight}dvh` : '',
        }}
        className={styles.container}>
        <div className={styles.drawer} ref={scrollAreaRef} style={{ paddingBottom: isScrollingMenu ? 70 : '' }}>
          <div className={styles.dragHandle} onClick={handleClose}>
            <span />
          </div>

          {props.customOptionsTop}

          {groupedOptions?.length && (
            <div className={styles.primarySecondaryOptions}>
              <div className={styles.options} ref={primaryOptionsRef}>
                {groupedOptions.map((options: Option[], groupIndex: number) => (
                  <DrawerMenuOptions
                    key={groupIndex}
                    options={options}
                    onOptionClicked={handleOptionClick}
                    onExtraOptionsClicked={optionId => {
                      const selectedExtras: Option | undefined = props.options?.find(opt => opt.id === optionId)

                      if (!selectedExtras) return
                      if (selectedExtras.type !== 'extras') return

                      setExtraOptionId(optionId)

                      if (selectedExtras.onExtrasOpened) selectedExtras.onExtrasOpened()

                      scrollAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' })

                      primarySecondaryCallbacks.handleSwipeInSecondary()
                    }}
                  />
                ))}
              </div>

              <div
                ref={secondaryOptionsRef}
                style={{
                  maxHeight: primaryOptionsRef.current?.clientHeight,
                  paddingBottom: isSecondaryScrollingMenu ? 140 : undefined,
                }}
                className={styles.secondaryOptions}>
                {groupedExtras.map((option: Option[], groupIndex: number) => (
                  <DrawerMenuOptions options={option} key={groupIndex} onOptionClicked={handleOptionClick} />
                ))}
              </div>
            </div>
          )}

          {props.customOptionsBottom}
        </div>

        {(isScrollingMenu || isSecondaryScrollingMenu) && <div className={styles.bottomGradient} />}
      </div>
    </div>,
    document.body,
  )
}

export default DrawerMenu

type DrawerMenuOptionsProps = {
  options: Option[]
  onOptionClicked: (onClick?: () => void, noCloseOnClick?: boolean, closeSecondaryOnClick?: boolean) => void
  onExtraOptionsClicked?: (optionId: string) => void
}

const DrawerMenuOptions = ({ options, onOptionClicked, onExtraOptionsClicked }: DrawerMenuOptionsProps) => {
  if (!options.length) return null

  // prettier-ignore
  if (options.length === 1 && options[0]?.type === 'header') return <div className={styles.header}>{options[0].label}</div>

  return (
    <div className={styles.group}>
      {options.map((option, optionIndex) => {
        if (option.hidden) return null

        if (option.to)
          return (
            <Link
              key={optionIndex}
              href={option.to}
              rel={option.rel}
              target={option.target}
              className={option.className}
              onClick={option.onClick}
              aria-label={option.ariaLabel}>
              {option.manaFontIcon && <ManaFrontIcon style={{ marginRight: '.55rem' }} icon={option.manaFontIcon} />}
              {option.icon && <Icon style={{ marginRight: '.55rem' }} name={option.icon} />}
              {option.customIcon && <CustomIcon style={{ marginRight: '.55rem' }} name={option.customIcon} />}
              {option.label}
            </Link>
          )

        if (option.type === 'custom')
          return (
            <div key={optionIndex} className={option.className}>
              {option.customChild}
            </div>
          )

        if (option.onClick)
          return (
            <SimpleButton
              id={option.id}
              disabled={option.disabled}
              key={optionIndex}
              icon={option.icon}
              manaFontIcon={option.manaFontIcon}
              customIcon={option.customIcon}
              className={option.className}
              onClick={e => onOptionClicked(option.onClick, option.noCloseOnClick, option.closeSecondaryOnClick)}>
              {option.label}
            </SimpleButton>
          )

        if (option.type === 'extras')
          return (
            <SimpleButton
              id={option.id}
              disabled={option.disabled}
              key={optionIndex}
              icon={option.icon}
              manaFontIcon={option.manaFontIcon}
              customIcon={option.customIcon}
              className={`${styles.extraMenuTrigger} ${option.className}`}
              onClick={() => (onExtraOptionsClicked ? onExtraOptionsClicked(option.id) : null)}>
              {option.label} <Icon name="chevron right" />
            </SimpleButton>
          )
        if (option.type === 'checkbox')
          return (
            <SimpleButton
              key={optionIndex}
              icon={option.checked ? 'check square' : 'square outline'}
              className={option.className}
              disabled={option.disabled}
              onClick={() => option.onChange()}>
              {option.label}
            </SimpleButton>
          )
      })}
    </div>
  )
}
